import { useMemo, useState } from "react";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tooltip, Typography, TextField } from "@mui/material";
import { colors } from "../../../../theme/muiColors";

import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

import { KanbanCategory } from "../../interfaces/kanbanCategory.interface";
import { useCreateKanbanCategory } from "../../hooks/useCreateKanbanCategories";
import { useUpdateKanbanCategories } from "../../hooks/useUpdateKanbanCategories";
import { useDeleteKanbanCategory } from "../../hooks/useDeleteKanbanCategories";
import { useGlobalSnackbar } from "../../../../context/SnackbarContext";
import { HelpOutline } from "@mui/icons-material";

interface KanbanCategorySelectorProps {
  categories: KanbanCategory[];
  selectedCategoryId: string | null;
  onSelectCategory: (categoryId: string) => void;
  kanbanPageUid: string;
  isPreviewMode?: boolean;
}

const ITEM_WIDTH = 200;

const ITEM_INNER_WIDTH = `calc(${ITEM_WIDTH}px - 20px)`;

const Sx = {
  row: {
    display: "flex",
    alignItems: "center",
    gap: 0.25,

    width: ITEM_INNER_WIDTH,
    maxWidth: ITEM_INNER_WIDTH,
    minWidth: 0,
  },

  categoryContainer: {
    display: "flex",
    pt: 2.75,
    gap: 2,
    flexWrap: "wrap",
    alignItems: "center",
    flexDirection: "column",

    width: "auto",
    minWidth: `${ITEM_WIDTH}px`,
    height: "inherit",

    bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
  },

  input: {
    flex: 1,
    minWidth: 0,

    "& .MuiInputBase-root": {
      height: 28,
      fontSize: "0.85rem",
    },

    "& input": {
      padding: "4px 8px",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  },

  item: (isSelected: boolean) => ({
    ...Sx.row,

    minHeight: "40px",

    borderRadius: 2,
    cursor: "pointer",
    padding: "4px 8px",

    border: "1px solid transparent",

    bgcolor: isSelected ? "var(--fx-surface-muted, #F1F5F9)" : "transparent",
    color: isSelected ? "var(--fx-surface-text, #0F172A)" : colors.grey[400],

    transition: "all 0.1s ease-in-out",

    "&:hover": {
      bgcolor: isSelected ? "var(--fx-surface-muted, #F1F5F9)" : "transparent",
      color: isSelected ? "var(--fx-surface-text, #0F172A)" : colors.grey[400],
      border: isSelected ? "1px solid transparent" : `1px solid ${colors.grey[300]}`,
    },
  }),

  itemName: {
    flex: 1,
    minWidth: 0,

    userSelect: "none",

    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },

  itemActions: {
    flexShrink: 0,

    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",

    gap: 0.25,
  },

  actionBtn: {
    color: "var(--fx-surface-texttertiary, #94A3B8)",

    transition: "transform 120ms ease, background-color 120ms ease, color 120ms ease",

    "&:hover": {
      color: "var(--fx-surface-text, #0F172A)",
      transform: "translateY(-2px)",
    },
  },

  sectionTitle: {
    fontSize: "0.8rem",
    fontWeight: 500,
    color: colors.grey[500],
  },
};

export const KanbanCategorySelector = ({
  categories,
  selectedCategoryId,
  onSelectCategory,
  kanbanPageUid,
  isPreviewMode = false,
}: KanbanCategorySelectorProps) => {
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");

  const [editingCategoryUid, setEditingCategoryUid] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");

  const [deleteTarget, setDeleteTarget] = useState<KanbanCategory | null>(null);

  const createCategoryMutation = useCreateKanbanCategory();
  const updateCategoryMutation = useUpdateKanbanCategories();
  const deleteCategoryMutation = useDeleteKanbanCategory(kanbanPageUid);

  const { showSnackbar } = useGlobalSnackbar();

  const sortedCategories = useMemo(() => [...categories].sort((a, b) => (a.order || 0) - (b.order || 0)), [categories]);

  const isBusy = createCategoryMutation.isPending || updateCategoryMutation.isPending || deleteCategoryMutation.isPending;

  const isDeleting = deleteCategoryMutation.isPending;
  const isCreating = createCategoryMutation.isPending;
  const isUpdating = updateCategoryMutation.isPending;

  const canModify = !isPreviewMode && !isBusy;

  const resetNewCategoryState = () => {
    setShowNewCategoryInput(false);
    setNewCategoryName("");
  };

  const startEdit = (category: KanbanCategory) => {
    setEditingCategoryUid(category.uid);
    setEditingName(category.name);
  };

  const cancelEdit = () => {
    setEditingCategoryUid(null);
    setEditingName("");
  };

  const handleCreateCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) return;

    try {
      const newCategory = await createCategoryMutation.mutateAsync({ name, kanbanPageUid });
      resetNewCategoryState();
      onSelectCategory(newCategory.uid);
      showSnackbar("Category created successfully!", "success", 3000, "bottom-left");
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Failed to create category. Please try again.";
      showSnackbar(msg, "error", 6000, "bottom-left");
    }
  };

  const submitEdit = async () => {
    if (!editingCategoryUid) return;

    const name = editingName.trim();
    if (!name) {
      showSnackbar("Category name cannot be empty.", "error", 4000, "bottom-left");
      return;
    }

    try {
      await updateCategoryMutation.mutateAsync({
        categoryUid: editingCategoryUid,
        patch: { name },
      });

      showSnackbar("Category updated.", "success", 2500, "bottom-left");
      cancelEdit();
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Failed to update category.";
      showSnackbar(msg, "error", 6000, "bottom-left");
    }
  };

  const confirmDelete = (category: KanbanCategory) => {
    setDeleteTarget(category);
  };

  const closeDeleteDialog = () => {
    setDeleteTarget(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      await deleteCategoryMutation.mutateAsync(deleteTarget.uid);

      if (selectedCategoryId === deleteTarget.uid) {
        const remaining = sortedCategories.filter((c) => c.uid !== deleteTarget.uid);
        if (remaining.length) onSelectCategory(remaining[0].uid);
      }

      showSnackbar("Category deleted.", "success", 2500, "bottom-left");
      closeDeleteDialog();
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Failed to delete category.";
      showSnackbar(msg, "error", 6000, "bottom-left");
    }
  };

  const handleEditKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") submitEdit();
    if (e.key === "Escape") cancelEdit();
  };

  const handleNewKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleCreateCategory();
    if (e.key === "Escape") resetNewCategoryState();
  };

  return (
    <>
      <Box sx={Sx.categoryContainer}>
        <Typography sx={Sx.sectionTitle}>Sections</Typography>

        {sortedCategories.map((category) => {
          const isSelected = selectedCategoryId === category.uid;
          const isEditing = editingCategoryUid === category.uid;

          if (isEditing) {
            return (
              <Box key={category.uid} sx={Sx.row}>
                <TextField
                  size="small"
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  onKeyDown={handleEditKeyDown}
                  autoFocus
                  sx={Sx.input}
                />
                <IconButton size="small" onClick={submitEdit} disabled={isUpdating}>
                  {isUpdating ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
                </IconButton>
                <IconButton size="small" onClick={cancelEdit} disabled={isUpdating}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            );
          }

          return (
            <Box key={category.uid} sx={Sx.item(isSelected)} onClick={() => onSelectCategory(category.uid)} onDoubleClick={() => startEdit(category)}>
              {/* 80% */}
              <Typography sx={Sx.itemName}>{category.name}</Typography>

              {/* 20% (only visible when selected) */}
              <Box sx={Sx.itemActions}>
                {isSelected && (
                  <>
                    <Tooltip title="Rename">
                      <span>
                        <IconButton
                          size="small"
                          sx={Sx.actionBtn}
                          onClick={(e) => {
                            e.stopPropagation();
                            startEdit(category);
                          }}
                          disabled={!canModify}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title="Delete">
                      <span>
                        <IconButton
                          size="small"
                          sx={Sx.actionBtn}
                          onClick={(e) => {
                            e.stopPropagation();
                            confirmDelete(category);
                          }}
                          disabled={!canModify}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </>
                )}
              </Box>
            </Box>
          );
        })}

        {showNewCategoryInput && !isPreviewMode ? (
          <Box sx={Sx.row}>
            <TextField
              size="small"
              placeholder="New Category"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              onKeyDown={handleNewKeyDown}
              autoFocus
              sx={Sx.input}
            />
            <IconButton size="small" onClick={handleCreateCategory} disabled={isCreating}>
              {isCreating ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
            </IconButton>
            <IconButton size="small" onClick={resetNewCategoryState} disabled={isCreating}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : isPreviewMode ? null : (
          <IconButton size="small" onClick={() => setShowNewCategoryInput(true)} disabled={isBusy}>
            <AddIcon />
          </IconButton>
        )}
      </Box>

      <Dialog
        open={!!deleteTarget}
        onClose={closeDeleteDialog}
        sx={{ zIndex: 999999999 }}
        PaperProps={{
          sx: {
            width: "100%",
            maxWidth: 444,
            borderRadius: "16px",
            border: `1px solid ${colors.orange[400]}`,
            overflow: "hidden",
            boxShadow: "0 18px 45px rgba(0, 0, 0, 0.12)",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: 3,
            py: 2.5,
            bgcolor: `${colors.orange[500]}10`,
            borderBottom: `1px solid ${colors.orange[300]}`,
            fontSize: "1.05rem",
            fontWeight: 700,
            color: colors.grey[900],
          }}
        >
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: "10px",
              border: `1px solid ${colors.orange[300]}`,
              bgcolor: "var(--fx-surface-base, #FFFFFF)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                bgcolor: colors.orange[500],
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <HelpOutline sx={{ fontSize: 18 }} />
            </Box>
          </Box>
          Delete Category
        </DialogTitle>

        <DialogContent
          sx={{
            px: 3,
            pt: 3,
            pb: 1,
            overflow: "hidden",
          }}
        >
          <Typography
            sx={{
              fontSize: "0.9rem",
              color: colors.grey[900],
              my: 1.5,
            }}
          >
            Are you sure you want to delete this category?
          </Typography>

          <Box
            title={deleteTarget?.name || ""}
            sx={{
              px: 1.25,
              py: 1,
              mb: 1.5,
              borderRadius: "6px",
              border: `1px solid ${colors.grey[300]}`,
              bgcolor: colors.grey[50],
              maxWidth: "100%",
              overflow: "hidden",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.85rem",
                fontWeight: 700,
                color: colors.grey[900],
                lineHeight: 1.35,

                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 2,

                overflow: "hidden",
                textOverflow: "ellipsis",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
            >
              {deleteTarget?.name}
            </Typography>
          </Box>

          <Typography
            sx={{
              fontSize: "0.85rem",
              color: colors.grey[800],
            }}
          >
            All tasks in this category will also be deleted. This action cannot be undone.
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pt: 3,
            pb: 3,
            gap: 2,
            justifyContent: "flex-end",
          }}
        >
          <Button
            onClick={closeDeleteDialog}
            disabled={isDeleting}
            variant="outlined"
            sx={{
              minWidth: 94,
              height: 44,
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 700,
              color: colors.grey[900],
              borderColor: colors.grey[400],
              bgcolor: "var(--fx-surface-base, #FFFFFF)",
              "&:hover": {
                borderColor: colors.grey[500],
                bgcolor: colors.grey[50],
              },
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleDelete}
            disabled={isDeleting}
            variant="contained"
            sx={{
              minWidth: 99,
              height: 44,
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 700,
              bgcolor: colors.orange[500],
              color: "#fff",
              boxShadow: "0 8px 16px rgba(249, 115, 22, 0.28)",
              "&:hover": {
                bgcolor: colors.orange[600],
                boxShadow: "0 8px 16px rgba(249, 115, 22, 0.32)",
              },
            }}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
